import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslationService } from '@/layout/service/translation.service';
import { translateCount } from '@/layout/service/translate-count';
import { RoomListRow } from '@/cloud/models/room-filters';
import { StatusBadgeComponent } from '@/shared/ui/status-badge/status-badge.component';
import { RoomUsageSummaryComponent } from './room-usage-summary.component';
import { RoomStatusPillComponent } from './room-status-pill.component';
import { RoomEnvironmentComponent } from './room-environment.component';
import { RoomRatingComponent } from './room-rating.component';

/**
 * A room in the rooms grid: name and type, the room's single status (with its calendar line), people
 * against capacity (always shown, "Capacity not set" when unknown), call state, live temperature /
 * humidity / light when the room measures them, the average user rating when the room has feedback, control system and device count. The whole card links to the room; the "Set capacity" action sits below the link
 * so it is not nested inside it.
 */
@Component({
    selector: 'app-room-card',
    standalone: true,
    imports: [RouterModule, StatusBadgeComponent, RoomUsageSummaryComponent, RoomStatusPillComponent, RoomEnvironmentComponent, RoomRatingComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <a class="room-card" [routerLink]="['/dashboard', room().appNamespaceId, room().id]">
            <div class="room-card-heading">
                <span class="room-card-icon"><i class="fa fa-door-open" aria-hidden="true"></i></span>
                <div>
                    <h2>{{ room().displayName }}</h2>
                    <p>
                        <span class="room-card-type" [class.room-card-type--unset]="!room().roomType">{{ t()(room().roomType ? 'roomType.' + room().roomType : 'roomType.notSet') }}</span>
                        @if (room().parentName) {
                            <span class="room-card-parent">{{ room().parentName }}</span>
                        }
                    </p>
                </div>
                <i class="pi pi-arrow-up-right room-card-arrow" aria-hidden="true"></i>
            </div>

            <div class="room-card-state">
                @if (room().usage; as usage) {
                    <app-room-usage-summary class="room-card-usage" [usage]="usage" [stale]="room().usageStale" [compact]="true" [showPeople]="false" />
                } @else {
                    <app-room-status-pill status="noData" />
                }
            </div>

            <div class="room-card-meta">
                <span class="room-card-capacity" [class.room-card-capacity--unset]="room().capacity === null && people() === null">
                    <i class="fa fa-person" aria-hidden="true"></i>
                    @if (people() !== null) {
                        <span class="room-card-people"
                            ><strong>{{ people() }}</strong>
                            @if (room().capacity !== null) {
                                / {{ room().capacity }}
                            }
                            {{ t()('roomUsage.people') }}</span
                        >
                        @if (fill() !== null) {
                            <span class="room-card-bar" role="presentation"><span [style.width.%]="fill()" [class.room-card-bar--full]="fill()! >= 100"></span></span>
                        }
                    } @else if (room().capacity !== null) {
                        <span>{{ seats() }}</span>
                    } @else {
                        <span>{{ t()('roomCapacity.notSet') }}</span>
                    }
                </span>
                @if (room().usage?.hasConferencing && !room().usage?.inCall) {
                    <span class="room-card-call"><i class="fa fa-video-slash" aria-hidden="true"></i> {{ t()('roomCall.notInCall') }}</span>
                }
                <app-room-environment [usage]="room().usage" [stale]="room().usageStale" [compact]="true" />
                @if (room().feedback) {
                    <app-room-rating class="room-card-rating" [feedback]="room().feedback" [showCount]="true" empty="hidden" />
                }
            </div>

            <div class="room-card-footer">
                <span class="room-card-system">
                    <i class="pi pi-server" aria-hidden="true"></i>
                    <span>{{ room().systemName || t()(room().controlSystemDeviceId ? 'location.systemUnavailable' : 'location.noControlSystem') }}</span>
                    @if (room().status; as status) {
                        <app-status-badge [status]="status" size="sm" />
                    }
                </span>
                <span class="room-card-devices"><i class="pi pi-desktop" aria-hidden="true"></i> {{ room().deviceCount ?? '—' }} {{ t()('room.devices') }}</span>
                @if (room().code) {
                    <span class="room-card-code">{{ room().code }}</span>
                }
            </div>
        </a>
        @if (canManage() && room().cloudCapacity === null) {
            <button type="button" class="room-card-cta" (click)="editProfile.emit(room())"><i class="pi pi-plus" aria-hidden="true"></i> {{ t()('roomCapacity.set') }}</button>
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                min-width: 0;
                border: 1px solid var(--surface-border);
                border-radius: var(--app-card-radius);
                background: var(--surface-card);
                overflow: hidden;
                transition: border-color 0.15s ease;
            }
            :host(:hover) {
                border-color: var(--primary-color);
            }
            .room-card {
                display: flex;
                flex: 1;
                flex-direction: column;
                gap: 0.875rem;
                min-width: 0;
                padding: 1.125rem 1.25rem 1rem;
                color: var(--text-color);
                text-decoration: none;
                transition: background-color 0.15s ease;
            }
            .room-card:hover {
                background: color-mix(in srgb, var(--primary-color) 4%, var(--surface-card));
            }
            .room-card:focus-visible {
                outline: 2px solid var(--primary-color);
                outline-offset: -2px;
                border-radius: var(--app-card-radius);
            }
            .room-card-heading {
                display: flex;
                align-items: flex-start;
                gap: 0.75rem;
            }
            .room-card-heading > div {
                flex: 1;
                min-width: 0;
            }
            .room-card-heading h2 {
                margin: 0;
                font-size: 1.05rem;
                font-weight: 600;
                line-height: 1.3;
                overflow-wrap: anywhere;
            }
            .room-card-heading p {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 0.25rem 0.5rem;
                margin: 0.25rem 0 0;
                color: var(--text-color-secondary);
                font-size: 0.8rem;
                overflow-wrap: anywhere;
            }
            .room-card-type {
                font-weight: 500;
                color: var(--text-color);
            }
            .room-card-type--unset {
                font-weight: 400;
                color: var(--text-color-secondary);
                font-style: italic;
            }
            .room-card-parent {
                min-width: 0;
            }
            .room-card-type + .room-card-parent::before {
                content: '·';
                margin-right: 0.5rem;
            }
            .room-card-icon {
                display: grid;
                place-items: center;
                flex-shrink: 0;
                width: 2.5rem;
                height: 2.5rem;
                border-radius: 0.75rem;
                color: var(--primary-color);
                background: color-mix(in srgb, var(--primary-color) 12%, transparent);
            }
            .room-card-arrow {
                color: var(--text-color-secondary);
            }
            .room-card-state {
                min-width: 0;
            }
            .room-card-meta {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 0.5rem 1rem;
                font-size: 0.8125rem;
                color: var(--text-color-secondary);
            }
            .room-card-capacity,
            .room-card-call {
                display: inline-flex;
                align-items: center;
                gap: 0.4rem;
                min-width: 0;
                font-variant-numeric: tabular-nums;
            }
            .room-card-capacity--unset {
                font-style: italic;
            }
            .room-card-people strong {
                color: var(--text-color);
                font-weight: 600;
            }
            .room-card-bar {
                display: inline-block;
                width: 3.5rem;
                height: 0.3rem;
                border-radius: 999px;
                background: var(--app-surface-inset-strong, var(--surface-border));
                overflow: hidden;
            }
            .room-card-bar > span {
                display: block;
                height: 100%;
                border-radius: inherit;
                background: var(--app-status-pending-fg);
            }
            .room-card-bar > .room-card-bar--full {
                background: var(--app-status-warning-fg);
            }
            .room-card-footer {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 0.5rem 1rem;
                margin-top: auto;
                padding-top: 0.75rem;
                border-top: 1px solid var(--surface-border);
                color: var(--text-color-secondary);
                font-size: 0.8rem;
                overflow-wrap: anywhere;
            }
            .room-card-footer > span {
                min-width: 0;
            }
            .room-card-system {
                display: inline-flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 0.4rem;
                flex: 1 1 12rem;
            }
            .room-card-system > span {
                min-width: 0;
                overflow-wrap: anywhere;
            }
            .room-card-code {
                font-family: var(--font-family-monospace, monospace);
                font-size: 0.75rem;
            }
            .room-card-cta {
                display: flex;
                align-items: center;
                gap: 0.4rem;
                width: 100%;
                padding: 0.55rem 1.25rem;
                border: 0;
                border-top: 1px dashed var(--surface-border);
                background: transparent;
                color: var(--primary-color);
                font: inherit;
                font-size: 0.8125rem;
                font-weight: 500;
                text-align: left;
                cursor: pointer;
            }
            .room-card-cta:hover {
                background: var(--surface-hover);
            }
            .room-card-cta:focus-visible {
                outline: 2px solid var(--primary-color);
                outline-offset: -2px;
            }
        `
    ]
})
export class RoomCardComponent {
    readonly t = inject(TranslationService).t;

    readonly room = input.required<RoomListRow>();
    /** The viewer may set the room's capacity and type (locations.manage). */
    readonly canManage = input(false);
    readonly editProfile = output<RoomListRow>();

    readonly people = computed(() => {
        const count = this.room().usage?.peopleCount;
        return typeof count === 'number' ? count : null;
    });
    /** People as a share of capacity (0..100), when both are known. */
    readonly fill = computed(() => {
        const people = this.people();
        const capacity = this.room().capacity;
        return people === null || !capacity ? null : Math.min(100, Math.round((people / capacity) * 100));
    });
    readonly seats = computed(() => translateCount(this.t(), 'roomCapacity.seats', this.room().capacity));
}
